import React from 'react';
import styles from './NoticeIcon.module.scss';

/**
 * `info` explains what will happen, `caution` flags something that changes what confirm does, and
 * `success` confirms there is nothing left to do. Pair each with the matching `.noticeInfo` /
 * `.noticeCaution` / `.noticeReady` class on the surrounding callout.
 */
export type NoticeVariant = 'info' | 'caution' | 'success';

interface NoticeIconProps {
  variant: NoticeVariant;
  className?: string;
}

/**
 * The glyph inside a rescue-portal notice callout: a circled "i", a circled "!", or a bare check.
 * Decorative — the notice's own text carries the meaning, so it stays out of the accessibility tree.
 *
 * Shared rather than inlined per modal: the same eleven lines were pasted into seven callouts across
 * four files, which is how a set of icons drifts into looking like several.
 */
export default function NoticeIcon({ variant, className }: NoticeIconProps) {
  return (
    <svg
      className={className ? `${styles.noticeIcon} ${className}` : styles.noticeIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      // The check is a bare polyline with corners to join, so it carries a touch more weight than
      // the two circled glyphs. Kept per-variant to preserve how each already renders.
      strokeWidth={variant === 'success' ? '2.4' : '2.2'}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {variant === 'success' ? (
        <polyline points="20 6 9 17 4 12" />
      ) : (
        <>
          <circle cx="12" cy="12" r="9.5" />
          {variant === 'info' ? (
            <>
              {/* Stem below a floating dot: an "i". */}
              <line x1="12" y1="11" x2="12" y2="16.5" />
              <line x1="12" y1="7.6" x2="12" y2="7.7" />
            </>
          ) : (
            <>
              {/* Stem above a floating dot: a "!". */}
              <line x1="12" y1="7.5" x2="12" y2="13" />
              <line x1="12" y1="16.4" x2="12" y2="16.5" />
            </>
          )}
        </>
      )}
    </svg>
  );
}
